import type { CrremDisplayMetric } from "features/energy-analysis-dashboard/model/types";

import { useMemo } from "react";

import { toYearlyPoints } from "../lib/buildCorrectionStacks";
import { mapPathwayToLegacyCrremRows } from "../lib/mapPathwayToLegacyCrremRows";
import {
  hasCrremDashboardData,
  pickDashboardMetric,
  pickPathwayTargets,
  pickTrendScenarios,
  toPathwayScenarios,
} from "../lib/parseCrremDashboardApi";
import { isBaselineCorrections, isClimateOnlyCorrections } from "../model/constants";
import { useCrremDashboardScenarios } from "./useCrremDashboardScenarios";
import { useCrremPathwaySettings } from "./useCrremPathwaySettings";

export const useNewCrremAsLegacyRows = (
  projectId: number | string,
  _quantity: "consumption" | "emission" = "emission"
) => {
  const projectKey = String(projectId);
  const { config } = useCrremPathwaySettings(projectKey);
  const dashboardQuery = useCrremDashboardScenarios(projectKey);
  const dashboardScenarios = pickTrendScenarios(dashboardQuery.data, config.trendMethod);
  const baselineScenario = dashboardScenarios.find((scenario) => isBaselineCorrections(scenario.corrections));

  const rows = useMemo(() => {
    const mapMetric = (metric: CrremDisplayMetric) => {
      const baselineRows = baselineScenario ? pickDashboardMetric(baselineScenario, metric).rows : [];

      const visibleScenarios = dashboardScenarios.filter((s) => config.visibleCorrectionIds.includes(s.id));
      const currentScenario =
        visibleScenarios.find((s) => isClimateOnlyCorrections(s.corrections)) ??
        visibleScenarios[0] ??
        baselineScenario;

      const currentRows = currentScenario ? pickDashboardMetric(currentScenario, metric).rows : baselineRows;
      const pathwayScenarios = toPathwayScenarios(pickPathwayTargets(dashboardQuery.data, metric));
      const pathwayPoints =
        pathwayScenarios.find((scenario) => scenario.key === config.scenarioKey)?.points ??
        pathwayScenarios[0]?.points ??
        [];
      return mapPathwayToLegacyCrremRows({
        baselinePoints: toYearlyPoints(baselineRows),
        currentPoints: toYearlyPoints(currentRows),
        pathwayPoints,
        projectId,
      });
    };
    return {
      consumption: mapMetric("kwh"),
      emission: mapMetric("co2"),
    };
  }, [
    baselineScenario,
    config.scenarioKey,
    config.visibleCorrectionIds,
    dashboardQuery.data,
    dashboardScenarios,
    projectId,
  ]);

  return {
    ...rows,
    hasData: hasCrremDashboardData(dashboardQuery.data),
    isLoading: dashboardQuery.isLoading,
  };
};
